import { ApiClient } from '@listening/api-contract/api-client-tag';
import type {
  AnnouncementId,
  FeedbackRequest,
  PostCancelSurveyRequest,
  UpdatePreferencesRequest,
  UpdateProfileRequest,
} from '@listening/api-contract';
import type {
  CancelRequest,
  ChangePlanRequest,
  CheckoutConfigSurface,
  PortalSessionRequest,
  PreviewPlanChangeRequest,
  SubscribeRequest,
  UserPreferences as UserPreferencesDto,
} from '@listening/api-contract/schemas';
import { Effect } from 'effect';

/**
 * `GET /billing/checkout-config` — the rendered offer: plans with
 * server-resolved amounts, the trial length, the paywall copy variant, and why
 * the caller may not buy. Everything a purchase surface needs, and no price ids.
 */
export type CheckoutConfigRequest = {
  readonly promoCode?: string;
  readonly surface?: CheckoutConfigSurface;
};

// User preferences — single-row, auth-gated. Derived from the API contract
// (the GetPreferences `preferences` block) so backend field changes flow
// through.
export type UserPreferences = typeof UserPreferencesDto.Type;

export type UserPreferencesUpdates =
  (typeof UpdatePreferencesRequest.Type)['updates'];

// —— Read effects ——

export const getUserSummaryEffect = Effect.flatMap(ApiClient, (api) =>
  api.User.Summary(),
);

export const getDismissedAnnouncementsEffect = Effect.flatMap(
  ApiClient,
  (api) => api.User.GetDismissedAnnouncements(),
);

export const dismissAnnouncementEffect = (announcement: AnnouncementId) =>
  Effect.flatMap(ApiClient, (api) =>
    api.User.DismissAnnouncement({ payload: { announcement } }),
  );

export const getUserProfileEffect = Effect.flatMap(ApiClient, (api) =>
  api.User.GetProfile().pipe(Effect.map((r) => r.data)),
);

export const getPaymentSubscriptionsEffect = Effect.flatMap(ApiClient, (api) =>
  api.Payments.GetSubscriptions(),
);

export const getPaymentDetailsEffect = Effect.flatMap(ApiClient, (api) =>
  api.Money.GetPaymentDetails(),
);

export const getBillingStatusEffect = Effect.flatMap(ApiClient, (api) =>
  api.Billing.GetBillingStatus(),
);

export const getCheckoutConfigEffect = ({
  promoCode,
  surface,
}: CheckoutConfigRequest) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Billing.GetCheckoutConfig({
      query: {
        ...(promoCode === undefined ? {} : { promo_code: promoCode }),
        ...(surface === undefined ? {} : { surface }),
      },
    }),
  );

export const getReferralListEffect = Effect.flatMap(ApiClient, (api) =>
  api.User.ReferralList(),
);

export const getUserPreferencesEffect = Effect.flatMap(ApiClient, (api) =>
  api.User.GetPreferences().pipe(
    Effect.map((r): UserPreferences => ({
      voice: r.voice,
      most_recent_language: r.most_recent_language,
    })),
  ),
);

// —— Mutation effects ——

// Derived from the API contract so backend field changes flow through.
export type UpdateUserProfilePayload =
  (typeof UpdateProfileRequest.Type)['updates'];

export const updateUserProfileEffect = (changes: UpdateUserProfilePayload) =>
  Effect.flatMap(ApiClient, (api) =>
    api.User.UpdateProfile({ payload: { updates: changes } }).pipe(
      Effect.map((r) => r.data),
    ),
  );

export const updateUserPreferencesEffect = (updates: UserPreferencesUpdates) =>
  Effect.flatMap(ApiClient, (api) =>
    api.User.UpdatePreferences({ payload: { updates } }),
  );

/**
 * `POST /billing/subscribe` — the one subscription-creation path.
 *
 * Also the uncancel: a subscription that is merely `cancel_at_period_end` is
 * cleared and the call returns with nothing to confirm, which is why the plan
 * page's "resubscribe" button posts here rather than to a second endpoint.
 */
export const billingSubscribeEffect = (
  payload: SubscribeRequest,
  /**
   * The caller's own idempotency key. Omitted, the server derives one over a
   * 60-second window — enough for a double-click, not enough for a checkout
   * retried minutes later, which is why the paywall supplies its own.
   */
  idempotencyKey?: string,
) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Billing.Subscribe({
      payload,
      headers:
        idempotencyKey === undefined
          ? {}
          : { 'idempotency-key': idempotencyKey },
    }),
  );

/**
 * `POST /billing/confirm` — the eager post-Stripe sync.
 *
 * Called after Stripe.js returns and again by `/post-purchase`'s loader on the
 * redirect flow: it syncs from Stripe rather than trusting the client, so
 * calling it twice costs a read and settles the same truth. Its
 * `amount_usd` / `currency` / `trial_started` fields are what decide the
 * conversion pixels.
 */
export const billingConfirmEffect = Effect.flatMap(ApiClient, (api) =>
  api.Billing.Confirm(),
);

/**
 * The subscription-management writes. Requests carry a `plan_id`, never a price
 * id, and never a trial length — the server owns both.
 */
export const billingCancelEffect = (payload: CancelRequest) =>
  Effect.flatMap(ApiClient, (api) => api.Billing.Cancel({ payload }));

/**
 * `POST /billing/change-plan/preview` — what confirming would cost, before
 * anything is confirmed.
 *
 * Never cached: the amount it reports is the amount the change is held to, and
 * a preview read from cache is one the server will refuse.
 */
export const billingPreviewPlanChangeEffect = (
  payload: PreviewPlanChangeRequest,
) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Billing.PreviewPlanChange({ payload }),
  );

export const billingChangePlanEffect = (
  payload: ChangePlanRequest,
  /**
   * Minted once per preview, so a double-click on one confirmation collapses to
   * a single Stripe write. Omitted, the server derives one from the actor, the
   * plan and the preview's proration instant.
   */
  idempotencyKey?: string,
) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Billing.ChangePlan({
      payload,
      headers:
        idempotencyKey === undefined
          ? {}
          : { 'idempotency-key': idempotencyKey },
    }),
  );

/**
 * Minted on click, never on page load — a portal session expires in five
 * minutes, so a URL fetched with the page is dead by the time it is used. That
 * is also why `/billing/status` carries no portal URL to prefetch.
 */
export const billingPortalSessionEffect = (payload: PortalSessionRequest) =>
  Effect.flatMap(ApiClient, (api) => api.Billing.PortalSession({ payload }));

export const setReferredByCodeEffect = (referralCode: string) =>
  Effect.flatMap(ApiClient, (api) =>
    api.User.SetReferredBy({ payload: { referral_code: referralCode } }),
  );

export const deleteUserEffect = Effect.flatMap(ApiClient, (api) =>
  api.User.DeleteUser(),
);

// —— User feedback & surveys ——
//
// Non-core user-input submissions to the backend User API, grouped under a
// `Feedback` namespace so callsites read unambiguously: a forked
// `Feedback.submitCancellationSurvey(...)` is obviously fire-and-forget
// telemetry, NOT the cancel/delete action itself (those are
// `billingCancelEffect` / `deleteUserEffect`). Mirrors the
// `AudioItems` namespace pattern in audio-items.ts.

export type CancellationSurveyPayload =
  (typeof PostCancelSurveyRequest.Type)['data'];

// Records WHY a user canceled or deleted (reason + liked features). Pure
// analytics — every callsite forks it fire-and-forget and never gates the
// cancel/delete on its result.
const submitCancellationSurveyEffect = (payload: CancellationSurveyPayload) =>
  Effect.flatMap(ApiClient, (api) =>
    api.User.PostCancelSurvey({ payload: { data: payload } }),
  );

export type FeedbackReportPayload = typeof FeedbackRequest.Type;

// User-submitted issue report (mispronunciation / citation / other). Unlike the
// survey this is user-facing — callsites await it and toast on success/failure.
const submitFeedbackReportEffect = (payload: FeedbackReportPayload) =>
  Effect.flatMap(ApiClient, (api) => api.User.Feedback({ payload }));

export type PostPurchaseSurveyPayload = {
  acquisition_source:
    | 'INFLUENCER'
    | 'PODCAST'
    | 'INSTAGRAM_OR_FACEBOOK'
    | 'YOUTUBE_AD'
    | 'SEARCH_ENGINE'
    | 'WORD_OF_MOUTH'
    | 'OTHER';
  details?: string;
};

const submitPostPurchaseSurveyEffect = (payload: PostPurchaseSurveyPayload) =>
  Effect.flatMap(ApiClient, (api) =>
    api.User.PostPurchaseSurvey({
      payload: {
        data: payload,
      },
    }),
  );

export const Feedback = {
  submitCancellationSurvey: submitCancellationSurveyEffect,
  submitPostPurchaseSurvey: submitPostPurchaseSurveyEffect,
  submitReport: submitFeedbackReportEffect,
} as const;
